import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Quote, ArrowRight } from "lucide-react";
import { TESTIMONIALS } from "@/data/content";

export function FadeIn({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <nav
      className="brand-container flex flex-wrap items-center gap-1.5 py-5 text-xs text-slate-400"
      data-testid="breadcrumbs"
    >
      <Link to="/" className="transition-colors hover:text-brand-navy" data-testid="breadcrumb-home-link">
        Home
      </Link>
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-1.5">
          <ChevronRight className="size-3" />
          {item.href ? (
            <Link
              to={item.href}
              className="transition-colors hover:text-brand-navy"
              data-testid={`breadcrumb-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              {item.label}
            </Link>
          ) : (
            <span className="text-slate-600">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function PageHero({ eyebrow, title, lead, children }) {
  return (
    <section className="brand-panel-dark pb-20 pt-36 sm:pb-24 sm:pt-44" data-testid="page-hero">
      <div className="brand-container">
        <FadeIn>
          {eyebrow && (
            <p className="brand-eyebrow-on-dark">
              {eyebrow}
            </p>
          )}
          <h1 className="mt-4 max-w-3xl text-4xl font-light tracking-tight sm:text-5xl lg:text-6xl">
            {title}
          </h1>
          {lead && (
            <p className="mt-6 max-w-2xl text-lg font-light leading-relaxed text-slate-300">
              {lead}
            </p>
          )}
          {children}
        </FadeIn>
      </div>
    </section>
  );
}

export function TestimonialBand({ quotes = TESTIMONIALS, dark = true }) {
  return (
    <section
      className={`${dark ? "brand-panel-dark" : "brand-panel-pale text-brand-navy"} py-20 sm:py-28 lg:py-32`}
      data-testid="testimonial-band"
    >
      <div className="brand-container">
        <FadeIn>
          <p className={dark ? "brand-eyebrow-on-dark" : "brand-eyebrow"}>
            What people say
          </p>
        </FadeIn>
        <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-3">
          {quotes.map((q, i) => (
            <FadeIn key={i} delay={i * 0.12}>
              <figure className={`flex h-full flex-col border-l-2 ${dark ? "border-brand-gold/50" : "border-brand-gold"} pl-6`}>
                <Quote className="size-6 text-brand-gold" />
                <blockquote className={`mt-4 flex-1 text-lg font-light leading-relaxed ${dark ? "text-slate-200" : "text-slate-700"}`}>
                  "{q.text}"
                </blockquote>
                <figcaption className={`mt-5 text-xs font-semibold uppercase tracking-[0.2em] ${dark ? "text-slate-400" : "text-slate-500"}`}>
                  {q.source}
                </figcaption>
              </figure>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ServiceCard({ service, Icon, delay = 0 }) {
  return (
    <FadeIn delay={delay} className="h-full">
      <Link
        to={`/services/${service.slug}`}
        className="brand-card group flex h-full flex-col p-8"
        data-testid={`service-card-${service.slug}`}
      >
        <Icon className="size-7 text-brand-gold" />
        <h3 className="mt-8 text-lg font-medium text-brand-navy">{service.title}</h3>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500">
          {service.tagline}
        </p>
        <span className="brand-text-link mt-6 flex items-center gap-2">
          Read more <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </Link>
    </FadeIn>
  );
}

export function AccreditationStrip() {
  const items = [
    "The Times Best Law Firm 2026",
    "Legal 500 Leading Firm",
    "Chambers and Partners Leading Firm",
    "Legal Aid Agency Contracted",
  ];
  return (
    <div className="border-b border-slate-200 bg-white" data-testid="accreditation-strip">
      <div className="brand-container grid grid-cols-2 gap-x-4 gap-y-4 py-8 md:grid-cols-4 lg:gap-x-8">
        {items.map((item, i) => (
          <p
            key={i}
            className="text-center text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 md:whitespace-nowrap lg:text-[11px] lg:tracking-[0.18em]"
          >
            {item}
          </p>
        ))}
      </div>
    </div>
  );
}
